import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatCashAmount } from '../../../lib/utils';
import { GenericNote, GenericCoin } from '../../elements/';

/*
 * Coupure – Displays coin/note breakdown
 *
 * Before: rendered all denominations (0.01 → 500), even when empty.
 * Like showing every banknote in existence when someone only has a few €1 coins.
 *
 * Now: only shows denominations actually present.
 *
 */

type CoupureItem = {
  coupure: number | string;
  qty?: number;
  qty_mut?: number;
};

type Props = {
  cn: 'c' | 'n'; // c = coin, n = note
  coupures: CoupureItem[];
  currency?: string;
  show_total?: boolean;
  show_mutated?: boolean;
};

export const Coupure: React.FC<Props> = ({
  cn,
  coupures,
  currency = 'EUR',
  show_total = false,
  show_mutated = false,
}) => {
  const { t } = useTranslation();
  const cols = 2 + (show_mutated ? 1 : 0) + (show_total ? 1 : 0);

  const activeCoupures = (Array.isArray(coupures) ? coupures : [])
    .filter(item => {
      const qty = item.qty ?? 0;
      const qtyMut = item.qty_mut ?? 0;
      return qty > 0 || qtyMut > 0;
    })
    .sort((a, b) => Number(b.coupure) - Number(a.coupure));

  if (activeCoupures.length === 0) {
    return <div className="text-sm text-gray-500">{t('coupure.empty')}</div>;
  }

  return (
    <>
      {activeCoupures.map((item) => {
        const value = Number(item.coupure);
        const qty = item.qty ?? 0;
        const mut = item.qty_mut ?? 0;
        const totalQty = qty + mut;
        const totalAmount = totalQty * value;

        // Optional: sort by value descending (like real cash count)
        // We'll sort here if you want natural order
        return (
          <div
            key={value}
            className={`grid grid-cols-${cols} items-center gap-x-4 py-1.5`}
          >
            <div className="flex items-center gap-3">
              {cn === 'c' ? (
                <GenericCoin value={value} currency={currency} className="w-9 h-9" />
              ) : (
                <GenericNote value={value} currency={currency} className="w-16 h-8" />
              )}
              <span className="text-sm font-medium">
                {currency} {value % 1 === 0 ? value : value.toFixed(2)}
              </span>
            </div>

            <div className="text-right font-semibold text-sm">
              {qty}
            </div>

            {show_mutated && (
              <div className="text-right font-semibold text-orange-700 text-sm">
                {mut}
              </div>
            )}

            {show_total && (
              <div className="text-right font-medium text-sm">
                {formatCashAmount(totalAmount)}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default Coupure;
